import type { TenantAnalyticsAdapter, TenantAnalyticsEnvelope } from '../types';
import {
  type TikTokPixelPublicConfig,
  parseTikTokPixelPublicConfig,
} from './config';

/** PII-free by platform decision: no identify/advanced-matching surface. */
export interface TikTokPixelEventParameters {
  readonly content_id?: string;
  readonly content_category?: string;
  readonly content_name?: string;
  readonly contents?: readonly {
    readonly content_id: string;
    readonly price: number;
    readonly quantity: number;
  }[];
  readonly value?: number;
  readonly currency?: string;
}

export type TikTokPixelEventName =
  | 'ViewContent'
  | 'InitiateCheckout'
  | 'Purchase';

/**
 * Mirrors the ttq API: `page()` for page views (TikTok's page views are not
 * track events) and `track(name, params, { event_id })` for the rest. The
 * event_id is the canonical eventId for browser/Events-API deduplication.
 */
export interface TikTokPixelEventSink {
  page(): void | Promise<void>;
  track(
    eventName: TikTokPixelEventName,
    parameters: TikTokPixelEventParameters,
    options: { readonly event_id: string },
  ): void | Promise<void>;
}

export interface CreateTikTokPixelAdapterOptions {
  readonly publicConfig: unknown;
  readonly sink: TikTokPixelEventSink;
  readonly id?: string;
  readonly enabled?: boolean;
}

export function createTikTokPixelAdapter(
  options: CreateTikTokPixelAdapterOptions,
): TenantAnalyticsAdapter {
  const publicConfig = requirePublicConfig(options.publicConfig);
  const sink = options.sink;

  if (
    !sink ||
    typeof sink.page !== 'function' ||
    typeof sink.track !== 'function'
  ) {
    throw new Error('TikTok Pixel requires an event sink.');
  }

  return Object.freeze({
    id: options.id ?? `tiktok:${publicConfig.pixelCode}`,
    provider: 'tiktok',
    requiredConsent: Object.freeze(['advertising'] as const),
    enabled: options.enabled !== false,
    deliver: async (envelope: TenantAnalyticsEnvelope) => {
      if (!envelope.event.browserDocumentLocationSafe) {
        throw new Error(
          'TikTok Pixel delivery suppressed for an unsafe browser document location.',
        );
      }

      const event = envelope.event;

      // TikTok normalizes parameters in place, so pass mutable copies while
      // keeping the canonical event immutable for the other adapters.
      switch (event.name) {
        case 'page_view':
          await sink.page();
          return;

        case 'view_content':
        case 'begin_checkout':
          await sink.track(
            event.name === 'view_content' ? 'ViewContent' : 'InitiateCheckout',
            {
              content_id: event.contentId,
              content_category: event.contentType,
              ...(event.contentName ? { content_name: event.contentName } : {}),
              ...(event.value !== undefined
                ? { value: event.value, currency: event.currency }
                : {}),
            },
            { event_id: event.eventId },
          );
          return;

        case 'purchase':
          await sink.track(
            // TikTok's web purchase event.
            'Purchase',
            {
              contents: event.items.map((item) => ({
                content_id: item.itemId,
                price: item.price,
                quantity: item.quantity,
              })),
              value: event.value,
              currency: event.currency,
            },
            { event_id: event.eventId },
          );
          return;
      }
    },
  });
}

function requirePublicConfig(value: unknown): TikTokPixelPublicConfig {
  const publicConfig = parseTikTokPixelPublicConfig(value);

  if (!publicConfig) {
    throw new Error('Invalid TikTok Pixel public configuration.');
  }

  return publicConfig;
}
